import type {
  NavigationGuardNext,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric,
  RouteRecordRaw,
} from 'vue-router';
import { useQuasar } from 'quasar';
import { getMetaTags } from '@/utils/metaTags';
import AuthLayout from '@/layouts/authLayout.vue';
import NewAuthLayout from '@/layouts/newAuthLayout.vue';
import HomePage from '@/pages/HomePage.vue';
import NewHomePage from '@/pages/NewHomePage.vue';
import HelpPage from '@/pages/HelpPage.vue';
import MethodologyPage from '@/pages/MethodologyPage.vue';

// Navigation guard to handle POST requests and redirect to GET
const handlePostToGet = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext,
) => {
  // Check if this is a POST request (you can detect this from headers or query params)
  const isPostRequest = to.query.method === 'POST' || to.query._method === 'POST';
  
  if (isPostRequest) {
    // Remove the method indicator and redirect to GET
    const query = { ...to.query };
    delete query.method;
    delete query._method;
    
    // Redirect to the same route but as GET
    next({
      path: to.path,
      query: query,
      replace: true
    });
  } else {
    // Continue with normal GET request
    next();
  }
};

const beforeEnter = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext,
) => {
  const $q = useQuasar();
  const token = $q.localStorage.getItem('auth')
    ? JSON.parse($q.localStorage.getItem('auth')!).token
    : null;
  const metaTags = await getMetaTags(to) as any;
    to.meta = metaTags;
  
  
  if (token) {
    next(); 
  } else {
    next('/login');
  }
};

const routes: RouteRecordRaw[] = [
  {
    path: '',
    redirect: { name: 'NewHomePage' },
  },
  {
    path: '/logout',
    component: () => import('@/pages/LogoutPage.vue')
  },
  // {
  //   path: '/u/:slug(.*)?',
  //   name: 'ProfilePage',
  //   component: () => import('@/pages/ProfilePage.vue'),
  // },
  // {
  //   path: '/password',
  //   name: 'PasswordPage',
  //   component: () => import('@/pages/WebLockPage.vue'),
  // },
  {
    path: '/',
    name: 'NewAuthLayout',
    component: NewAuthLayout,
    children: [

    ]
  },
  {
    path: '/',
    name: 'AuthLayout',
    component: AuthLayout,
    children: [
          {
        path: '/',
        name: 'NewHomePage',
        component: NewHomePage,
      },
          {
        path: '/register-interest',
        name: 'JoinUsPage',
        component: () => import('@/pages/JoinUsPage.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
        beforeEnter: async (
          to: RouteLocationNormalizedGeneric,
          from: RouteLocationNormalizedLoadedGeneric,
          next: NavigationGuardNext,
        ) => {
          const metaTags = await getMetaTags(to) as any;
          to.meta = { ...to.meta, ...metaTags };
          next();
        },
      },
          {
        path: '/help',
        name: 'helpPage',
        component: HelpPage,
      },
          {
        path: '/methodology',
        name: 'methodologyPage',
        component: MethodologyPage,
      },

      {
        path: '/about-us',
        name: 'AboutUsPage',
        component: () => import('@/pages/AboutUsPage.vue'),
      },
      {
        path: '/expert',
        name: 'ExpertPage',
        component: () => import('@/pages/ExpertPage.vue'),
      },
      {
        path: '/rating',
        name: 'HomePage',
        component: HomePage,
      },
      {
        path: '/contact-us',
        name: 'ContactUsPage',
        component: () => import('@/pages/ContactUsPage.vue'),
      },
      {
        path: '/subscription/:url?/:plan?',
        name: 'SubscriptionPage',
        component: () => import('@/pages/SubscriptionPage.vue'),
      },
      {
        path: '/payment/status/:sub_id/:status?',
        name: 'PaymentStatusPage',
        component: () => import('@/pages/PaymentStatusPage.vue'),
        beforeEnter: handlePostToGet,

      },
      {
        path: '/reviews',
        name: 'ReviewProfilePage',
        component: () => import('@/pages/ReviewProfilePage.vue'),
        beforeEnter,
      },
      {
        path: '/business',
        name: 'BusinessProfilePage',
        component: () => import('@/pages/ProfileWebsitesPage.vue'),
        /* eslint-disable @typescript-eslint/no-unused-vars */
        beforeEnter: async (
          to: RouteLocationNormalizedGeneric,
          from: RouteLocationNormalizedLoadedGeneric,
          next: NavigationGuardNext,
        ) => {
          const $q = useQuasar();
          const localData = $q.localStorage.getItem('auth');
          const token = $q.localStorage.getItem('auth')
            ? JSON.parse($q.localStorage.getItem('auth')!).token
            : null;

          const is_agent = localData
            ? JSON.parse(localData.toString()).is_agent
            : null;
          if (token && is_agent == 1) {
            next();
            const metaTags = await getMetaTags(to) as any;
            to.meta = metaTags;
          } else {
            next('/profile');
          }
        },
      },
      {
        path: '/verified-reviewer',
        name: 'VerifiedReviewerPage',
        component: () => import('@/pages/VerifiedReviewerPage.vue'),
        beforeEnter,
      },
      {
        path: '/verified-reviewer/finish',
        name: 'VerifiedReviewerFinishPage',
        component: () => import('@/pages/VerifiedReviewerFinishPage.vue'),
        beforeEnter,
      },
      {
        path: '/verified-reviewer/edit',
        name: 'VerifiedReviewerEditPage',
        component: () => import('@/pages/VerifiedReviewerEditPage.vue'),
        beforeEnter,
      },
      {
        path: '/settings',
        name: 'ProfileSettingsPage',
        component: () => import('@/pages/ProfileSettingsPage.vue'),
        beforeEnter,
      },
   
      {
        path: '/add-rating/:url(.*)?',
        name: 'AddRatingPage',
        component: () => import('@/pages/AddRatingPage.vue'),
        beforeEnter,
      },
      {
        path: '/edit-rating/:id',
        name: 'EditRatingPage',
        component: () => import('@/pages/AddRatingPage.vue'),
        beforeEnter,
      },
      {
        path: '/verify',
        name: 'VerifyAccountPage',
        component: () => import('@/pages/VerifyAccountPage.vue'),
        beforeEnter,
      },
      {
        path: '/reviews/:id',
        name: 'ReviewPage',
        component: () => import('@/pages/ViewReviewPage.vue'),
      },
      {
        path: '/search',
        name: 'SearchResultsPage',
        component: () => import('@/pages/SearchResultsPage.vue'),
        props: (route) => ({
          term: route.query.term,
          page: Number(route.query.page) || 1,
        }),
      },
      {
        path: '/result/:url(.*)',
        name: 'ResultPage',
        component: () => import('@/pages/RatingReportPage.vue'),
        // component: RatingReportPage,
      },
      {
        path: '/report/:url(.*)',
        name: 'ResultDetailsPage',
        component: () => import('@/pages/DetailedRatingReportPage.vue'),
      },
      {
        path: '/privacy',
        name: 'PrivacyPage',
        component: () => import('@/pages/PrivacyPage.vue'),
      },
      {
        path: '/terms-conditions',
        name: 'TermsPage',
        component: () => import('@/pages/TermsPage.vue'),
      },
      {
        path: '/cx',
        name: 'CX',
        component: () => import('@/pages/cxPage.vue'),
      },
      {
        path: '/lab',
        name: 'LabPage',
        component: () => import('@/pages/LabPage.vue'),
      },
      {
        path: '/akwadion',
        name: 'akwadion',
        component: () => import('@/pages/ReviewerPage.vue'),
      },
      {
        path: '/:slug(.*)?',
        name: 'ProfilePage',
        component: () => import('@/pages/ProfilePage.vue'),
        
      },
      {
        path: '/orders-subscription',
        name: 'OrdersSubscriptionPage',
        component: () => import('@/pages/OrdersSubscriptionPage.vue'),
      },
      {
        path: '/orders-subscription/:id',
        name: 'OrdersSubscriptionDetailsPage',
        component: () => import('@/pages/OrdersSubscriptionDetailsPage.vue'),
        beforeEnter,
      },
      {
        path: '/wallet',
        name: 'wallet',
        component: () => import('@/pages/WalletPage.vue'),
        beforeEnter,
      },
      {
        path: '/bank-account/:action',
        name: 'BankAccountPage',
        component: () => import('@/pages/BankAccountPage.vue'),
        beforeEnter,
      },
      {
        path: '/wallet/withdraw',
        name: 'wallet-withdraw',
        component: () => import('@/pages/WalletWithdrawPage.vue'),
        beforeEnter,
      },
      {
        path: '/404',
        name: 'NotFound',
        component: () => import('@/pages/NotFoundPage.vue'),
        
      },
      {
        path: '/422',
        name: 'InvalidURL',
        component: () => import('@/pages/InvalidURLPage.vue'),
      },
      {
        path: '/403',
        name: 'Unauthorized',
        component: () => import('@/pages/UnauthorizedPage.vue'),
      },
    ],
  },
  {
    path: '/',
    name: 'UnAuthLayout',
    component: () => import('@/layouts/unAuthLayout.vue'),
    beforeEnter(to, from, next) {
      const $q = useQuasar();
      const token = $q.localStorage.getItem('auth')
        ? JSON.parse($q.localStorage.getItem('auth')!).token
        : null;


         if(to.name === 'lab-login'){
          next();
        }else {
          if(token ){
            next('/');
          
          }else{
            next();
          }
        }
     
    

    },
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/AuthPage.vue'),
      },
      {
        path: 'register/:codeInvite?',
        name: 'register',
        component: () => import('@/pages/AuthPage.vue'),
        props: true,
      },
  
      {
        path: 'verify-account',
        name: 'verify-account',
        component: () => import('@/pages/VerifyPhonePage.vue'),
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/ResetPasswordPage.vue'),
      },
      {
        path: 'lab/login',
        name: 'lab-login',
        component: () => import('@/pages/AuthPage.vue'),
      },
   
    ],
  },

  {
    path: '/account-restoration',
    name: 'account-restoration',
    component: () => import('@/pages/AccountRestorationPage.vue'),
  },
  {
    path: '/report/:id',
    name: 'PublicSubscriptionReportPage',
    component: () => import('@/pages/PublicSubscriptionReportPage.vue'),
  },
  // {
  //   path: '/:pathMatch(.*)*',
  //   name: 'NotFound',
  //   component: () => import('@/pages/NotFoundPage.vue'),
  // },
];

export default routes;
